import React from 'react';

import { PartnerManagerSummaryScreen } from 'containers/PartnerManager/PartnerManagerSummaryScreen';
import { PartnerManagerClientsScreen } from 'containers/PartnerManager/PartnerManagerClientsScreen';
import { PartnerManagerPasswordEditor } from 'containers/PartnerManager/PartnerManagerPasswordEditor';
import { PartnerManagerProfileEditor } from 'containers/PartnerManager/PartnerManagerProfileEditor';
import { PartnerManagerCredentialsEditor } from 'containers/PartnerManager/PartnerManagerCredentialsEditor';
import { ModalLayerComponent } from 'design/modalLayer';
import { SidebarModalWindowComponent } from 'design/sidebarModal';
import { usePartnerManager } from 'hooks/usePartnerManager';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { usePartnerManagerDataProcess } from 'hooks/usePartnerManagerDataProcess';
import { PartnerManagerHeader } from './PartnerManagerHeader';
import { PartnerFinancesTable } from 'containers/PartnerFinancesTable';
import { PartnerManagerRewardEditor } from './PartnerManagerRewardEditor';
import { PartnerFinanceMonthRewardIncome } from 'types/partnerFinance';

type Props = {
  partnerId: number;
  onDataEdited(): void;
  onViewClientDetails(clientId: number): void;
};

export const PartnerManager = (props: Props) => {
  const { partnerId, onDataEdited, onViewClientDetails } = props;

  usePartnerManagerDataProcess(partnerId);

  const { data: partnerManagerData } = usePartnerManager();

  const dispatch = useAppDispatcher();

  const { screen } = usePartnerManager();

  const goToSummaryScreen = React.useCallback(() => {
    dispatch({
      name: 'PartnerManager__SreenChanged',
      nextScreen: { name: 'SUMMARY' },
    });
  }, [dispatch]);

  const goToClientsScreen = React.useCallback(() => {
    dispatch({
      name: 'PartnerManager__SreenChanged',
      nextScreen: { name: 'CLIENTS' },
    });
  }, [dispatch]);

  const goToFinancesScreen = React.useCallback(() => {
    dispatch({
      name: 'PartnerManager__SreenChanged',
      nextScreen: { name: 'FINANCES' },
    });
  }, [dispatch]);

  const goToProfileEditorScreen = React.useCallback(() => {
    dispatch({
      name: 'PartnerManager__SreenChanged',
      nextScreen: { name: 'PROFILE_EDITOR' },
    });
  }, [dispatch]);

  const goToCredentialsScreen = React.useCallback(() => {
    dispatch({
      name: 'PartnerManager__SreenChanged',
      nextScreen: { name: 'CREDENTIALS_EDITOR' },
    });
  }, [dispatch]);

  const goToPasswordEditorScreen = React.useCallback(() => {
    dispatch({
      name: 'PartnerManager__SreenChanged',
      nextScreen: { name: 'PASSWORD_EDITOR' },
    });
  }, [dispatch]);

  const goToRewardEditorScreen = React.useCallback(
    (editedRewardIncome?: PartnerFinanceMonthRewardIncome) => {
      dispatch({
        name: 'PartnerManager__SreenChanged',
        nextScreen: { name: 'REWARD_EDITOR', editedRewardIncome },
      });
    },
    [dispatch]
  );

  const handleBlockChanged = React.useCallback(() => {
    dispatch({
      name: 'PartnerManager__BlockChanged',
    });
    onDataEdited();
  }, [dispatch, onDataEdited]);

  const close = React.useCallback(() => {
    dispatch({
      name: 'PartnerManager__Closed',
    });
  }, [dispatch]);

  const handleProfileEdited = React.useCallback(() => {
    goToSummaryScreen();
    onDataEdited();
  }, [goToSummaryScreen, onDataEdited]);

  const handlePasswordEdited = React.useCallback(() => {
    goToSummaryScreen();
    onDataEdited();
  }, [goToSummaryScreen, onDataEdited]);

  const handleRewardEdited = React.useCallback(() => {
    goToFinancesScreen();
    onDataEdited();
  }, [goToFinancesScreen, onDataEdited]);

  const hasCloseAction =
    screen.name === 'SUMMARY' ||
    screen.name === 'CLIENTS' ||
    screen.name === 'FINANCES';

  const handleClose = React.useCallback(() => {
    if (hasCloseAction) {
      close();
    }
  }, [close, hasCloseAction]);

  const screenNode = React.useMemo(() => {
    if (partnerManagerData) {
      switch (screen.name) {
        case 'SUMMARY':
          return (
            <>
              <PartnerManagerHeader
                currentScreen={screen}
                parnerManagerData={partnerManagerData}
                onViewSummary={goToSummaryScreen}
                onViewFinances={goToFinancesScreen}
                onViewClients={goToClientsScreen}
              />
              <PartnerManagerSummaryScreen
                partnerId={partnerId}
                parnerManagerData={partnerManagerData}
                onEditProfile={goToProfileEditorScreen}
                onEditCredentials={goToCredentialsScreen}
                onEditPassword={goToPasswordEditorScreen}
                onBlockChanged={handleBlockChanged}
              />
            </>
          );
        case 'CLIENTS':
          return (
            <>
              <PartnerManagerHeader
                currentScreen={screen}
                parnerManagerData={partnerManagerData}
                onViewSummary={goToSummaryScreen}
                onViewFinances={goToFinancesScreen}
                onViewClients={goToClientsScreen}
              />
              <PartnerManagerClientsScreen
                partnerId={partnerId}
                parnerManagerData={partnerManagerData}
                onViewClientDetails={onViewClientDetails}
              />
            </>
          );
        case 'FINANCES':
          return (
            <>
              <PartnerManagerHeader
                currentScreen={screen}
                parnerManagerData={partnerManagerData}
                onViewSummary={goToSummaryScreen}
                onViewFinances={goToFinancesScreen}
                onViewClients={goToClientsScreen}
              />
              <PartnerFinancesTable
                partnerFinances={partnerManagerData.partnerFinances}
                partnerFinanceMonthReports={
                  partnerManagerData.partnerFinanceMonthReports
                }
                onShowClientDetails={onViewClientDetails}
                onCreateReward={goToRewardEditorScreen}
                onEditReward={goToRewardEditorScreen}
              />
            </>
          );
        case 'PROFILE_EDITOR':
          return (
            <PartnerManagerProfileEditor
              partnerId={partnerId}
              parnerManagerData={partnerManagerData}
              onCancel={goToSummaryScreen}
              onEdited={handleProfileEdited}
            />
          );
        case 'PASSWORD_EDITOR':
          return (
            <PartnerManagerPasswordEditor
              partnerId={partnerId}
              onCancel={goToSummaryScreen}
              onEdited={handlePasswordEdited}
            />
          );
        case 'CREDENTIALS_EDITOR':
          return (
            <PartnerManagerCredentialsEditor
              partnerId={partnerId}
              parnerManagerData={partnerManagerData}
              onCancel={goToSummaryScreen}
              onEdited={handlePasswordEdited}
            />
          );
        case 'REWARD_EDITOR':
          return (
            <PartnerManagerRewardEditor
              partnerId={partnerId}
              editedRewardIncome={screen.editedRewardIncome}
              onCancel={goToFinancesScreen}
              onEdited={handleRewardEdited}
            />
          );
        default:
          return null;
      }
    }
    return null;
  }, [
    goToClientsScreen,
    goToCredentialsScreen,
    goToFinancesScreen,
    goToPasswordEditorScreen,
    goToProfileEditorScreen,
    goToRewardEditorScreen,
    goToSummaryScreen,
    handleBlockChanged,
    handlePasswordEdited,
    handleProfileEdited,
    handleRewardEdited,
    onViewClientDetails,
    partnerId,
    partnerManagerData,
    screen,
  ]);

  return (
    <ModalLayerComponent theme="transparent">
      <SidebarModalWindowComponent
        onMisclick={handleClose}
        onEscape={handleClose}
        onClose={hasCloseAction ? handleClose : undefined}
      >
        {screenNode}
      </SidebarModalWindowComponent>
    </ModalLayerComponent>
  );
};
